import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { sampleEnquirySchema } from "@/lib/sample-enquiry";
import { packageTerms } from "@/data/linkBuildingPackages";

const Hero = () => {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [company, setCompany] = useState("");
  const submittingRef = useRef(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submittingRef.current) return;
    const result = sampleEnquirySchema.safeParse({ website: websiteUrl, email, company });
    if (!result.success) {
      toast({
        title: "Please check your details",
        description: result.error.issues[0]?.message,
        variant: "destructive",
      });
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: "Quick Contact",
          email: result.data.email,
          website: result.data.website,
          message: "Please send me a sample list of publish-ready sites relevant to my website.",
          company: result.data.company,
          source: "homepage-sample-list",
        },
      });
      if (error) throw error;
      if (data?.success !== true) throw new Error("Submission was not confirmed");
      toast({
        title: "Thanks for reaching out!",
        description: "We’ll review your website and email you relevant sample sites.",
      });
      setWebsiteUrl("");
      setEmail("");
      setCompany("");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-8 md:pb-0">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--hero-gradient-start))] via-[hsl(var(--hero-gradient-end))] to-[hsl(var(--hero-gradient-start))]" />
      
      {/* Animated orbs - hidden on mobile to improve LCP */}
      <div className="hidden md:block absolute top-20 left-10 w-72 h-72 bg-accent/20 rounded-full blur-3xl animate-pulse" />
      <div className="hidden md:block absolute bottom-20 right-10 w-96 h-96 bg-primary-foreground/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-40 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      
      {/* Grid pattern - simplified on mobile */}
      <div className="absolute inset-0 opacity-5 md:opacity-10" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-5xl mx-auto text-center flex flex-col">

          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
            Quality Backlinks
            <span className="block mt-3 bg-gradient-to-r from-accent via-orange-400 to-orange-300 bg-clip-text text-transparent pb-2" style={{ lineHeight: '1.2' }}>
              For Your Business
            </span>
          </h1>
          
          {/* Supporting tagline */}
          <p className="max-w-2xl mx-auto text-base sm:text-lg text-white/80 leading-relaxed mb-4">
            Get niche-relevant guest posts on real websites with Ahrefs-verified organic traffic. Content and reporting included. No PBNs. No automated placements.
          </p>
          <div className="mb-6">
            <Link
              to="/b2b-link-building"
              className="inline-flex items-center text-sm text-accent font-medium hover:underline"
            >
              Explore our B2B link building services <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
            <span className="mx-2 text-white/50" aria-hidden="true">·</span>
            <Link
              to="/ai-visibility-audit"
              className="inline-flex items-center text-sm text-accent font-medium hover:underline"
            >
              Get a free AI visibility audit <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
            <span className="mx-2 text-white/50" aria-hidden="true">·</span>
            <Link
              to="/ai-visibility-checker"
              className="inline-flex items-center text-sm text-accent font-medium hover:underline"
            >
              Try the free AI visibility checker <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>

          <div className="order-2 md:order-none grid grid-cols-1 gap-4 md:grid-cols-3 w-full max-w-4xl mx-auto mb-8 mt-8 md:mt-0">
            {[
              { value: `From $${packageTerms[0].price.toLocaleString("en-US")}`, detail: `${packageTerms[0].links} guest posts, content included · USD` },
              { value: `DR ${packageTerms[0].minimumDR}+`, detail: "Host websites with Ahrefs-verified organic traffic" },
              { value: "Custom report", detail: "See where your links are placed" },
            ].map((item) => (
              <div key={item.value} className="p-5 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                <p className="text-2xl font-bold text-accent">{item.value}</p>
                <p className="mt-2 text-sm text-primary-foreground/80 leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>

          {/* Form Section */}
          <div className="order-1 md:order-none w-full max-w-3xl mx-auto px-2">
            {/* Form Header */}
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-white/70">
                <Sparkles className="w-4 h-4 text-accent" />
                Get a free sample of sites relevant to your website
              </span>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="relative p-1 rounded-[28px] bg-gradient-to-r from-accent/50 via-orange-400/50 to-accent/50">
                <div className="flex flex-col gap-4 p-4 sm:p-6 bg-white/10 backdrop-blur-xl rounded-3xl">
                  {/* Inputs Row */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Input 
                        type="text" 
                        placeholder="Your website (example.com)"
                        autoComplete="url"
                        autoCapitalize="none"
                        spellCheck={false} 
                        aria-label="Your website"
                        maxLength={500}
                        value={websiteUrl} 
                        onChange={e => setWebsiteUrl(e.target.value)} 
                        className="w-full border-0 bg-white text-foreground placeholder:text-muted-foreground text-base h-14 sm:h-12 rounded-xl px-4 focus-visible:ring-2 focus-visible:ring-accent shadow-sm" 
                        required 
                      />
                    </div>
                    <div className="flex-1 relative">
                      <Input 
                        type="email" 
                        placeholder="Your email"
                        autoComplete="email" 
                        aria-label="Your email"
                        maxLength={255}
                        value={email} 
                        onChange={e => setEmail(e.target.value)} 
                        className="w-full border-0 bg-white text-foreground placeholder:text-muted-foreground text-base h-14 sm:h-12 rounded-xl px-4 focus-visible:ring-2 focus-visible:ring-accent shadow-sm" 
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="sample-company">Company</label>
                    <Input id="sample-company" name="company" value={company} onChange={e => setCompany(e.target.value)} tabIndex={-1} autoComplete="off" />
                  </div>
                  
                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full whitespace-normal rounded-xl h-auto min-h-14 sm:min-h-12 py-3 bg-gradient-to-r from-accent to-orange-500 hover:from-accent/90 hover:to-orange-500/90 text-white font-semibold shadow-lg shadow-accent/30 gap-2 transition-all duration-300 hover:shadow-xl hover:shadow-accent/40 hover:scale-[1.02]" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send me relevant sample sites"}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </form>

          </div>

        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>

    <div className="container mx-auto px-4 md:px-6 text-center pb-3 pt-6">
      <p className="text-sm font-bold text-foreground">Free enquiry. No payment required to request sample sites.</p>
      <p className="mt-1 text-sm font-bold text-foreground">
        Ready to compare? <Link to="/pricing" className="font-bold text-accent underline underline-offset-4">View link building packages and prices</Link>.
      </p>
    </div>
    </>
  );
};

export default Hero;
